"use client";

import { useActionState, useEffect, useRef, useState } from "react";
import {
  captureJobAlert,
  type CaptureActionResult,
} from "@/app/jobs/alert-actions";
import { useJobPageAuth } from "@/components/JobPageAuth";
import { VideoApplicationCta } from "@/components/VideoApplicationCta";
import { capture } from "@/lib/analytics/events";
import {
  PREMIUM_BILLING_NOTE,
  PREMIUM_PRICE_LABEL,
} from "@/lib/portfolio/entitlements";

// CM-460 rendered the sidebar's `VideoApplicationCta` here and auto-opened
// its dialog on reveal; it still converted 1.6% (vs 0.5% before) with 1
// checkout in 6.7 days, so the gap is post-apply intent, not UI. CM-471
// leads with the alert and keeps the priced offer inline, click-to-open.

/**
 * The email ask, moved to the only moment on the site where intent is PROVEN:
 * the visitor has just come back from the employer's own application portal.
 *
 * T-247 on the sibling board measured the gap this closes — 677 apply clicks
 * in a week against 9 addresses left (0.3% of visitors). Everything we can
 * later sell, mail or win back depends on that number, and the board nudge
 * (AlertNudge) only fires on a refined search, which someone who arrived on a
 * job page from Google never performs.
 *
 * It replaces the micro-survey that used to occupy this moment in ApplyLink
 * ("was anything about this listing unclear?"), which answered 4 times against
 * 561 shown over 30 days. Same trigger, better ask.
 *
 * Two mechanics are inherited from that survey and are not incidental:
 *  - reveal on the hidden→visible transition, never on the click itself,
 *    because `target=_blank` means the click happens in a tab nobody is
 *    looking at any more;
 *  - a timeout fallback for the middle/cmd-click visitor who never leaves
 *    this tab, and therefore never produces a visibility change.
 *
 * It also carries the PAID offer (CM-439). Measured week of 2026-09-22: 1,281
 * people visited this board and 7 (0.5%) were ever shown what we sell. The
 * price/features teaser below renders on reveal, with nothing clicked, so
 * that count stays a true impression. Not shown to members —
 * they already have it — which is why this reads `isPaid`.
 */
export function PostApplyAlert({
  /** Alert-scope querystring for "more jobs like this one". */
  filters,
  /** Human summary of that scope, so the offer is concrete. */
  scopeLabel,
  jobId,
  jobTitle,
  orgName,
  applyUrl,
}: {
  filters: string;
  scopeLabel: string;
  jobId: number;
  jobTitle: string;
  orgName: string;
  applyUrl: string;
}) {
  const { isPaid, isAuthed } = useJobPageAuth();
  const [shown, setShown] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(false);
  const [result, formAction, pending] = useActionState<
    CaptureActionResult | null,
    FormData
  >(captureJobAlert, null);

  useEffect(() => {
    const reveal = () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      setShown(true);
    };
    const onApply = () => {
      armedRef.current = true;
      // Fallback for the middle/cmd-click visitor whose tab never blurs, so
      // `visibilitychange` never fires. A plain click DOES blur this tab, and
      // a real application on the employer's site takes minutes — longer
      // than this 8s window — so firing unconditionally revealed (and
      // counted as a "premium_pitch_view" impression) a panel nobody was
      // looking at, whose one scrollIntoView happened while hidden and never
      // repeated. Only fire the flat timeout while still visible; otherwise
      // defer to the visibilitychange listener below, which fires exactly
      // when the visitor is actually back to see the reveal happen.
      window.setTimeout(() => {
        if (document.visibilityState === "visible") reveal();
      }, 8000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") reveal();
    };
    window.addEventListener("changemaker:apply_click", onApply);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("changemaker:apply_click", onApply);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!shown) return;
    capture("alert_nudge_shown", { filters, surface: "job_post_apply" });
    // The offer is rendered, unclicked and priced, the moment this panel is —
    // so the impression is recorded here, not on a click. Same event name and
    // surface as Finseats, so one query answers "how many people saw what we
    // sell" across both boards.
    if (!isPaid) capture("premium_pitch_view", { surface: "job_post_apply" });
    // `smooth` depends on rAF, which browsers throttle at exactly the
    // hidden→visible transition this fires on, so the scroll never finishes.
    rootRef.current?.scrollIntoView({ behavior: "instant", block: "center" });
  }, [shown, filters, isPaid]);

  const captured = result?.ok === true;
  useEffect(() => {
    if (!captured) return;
    capture("alert_nudge_captured", {
      state: result?.state ?? "check_inbox",
      filters,
      surface: "job_post_apply",
    });
  }, [captured, result?.state, filters]);

  if (!shown) return null;

  // UN and NGO feeds put the whole vacancy notice in the title field — the one
  // this rendered against locally was 118 characters ("National consultant
  // Face-to-Face (F2F) Fundraiser for Croatia Country Office(m/f), Zagreb,
  // Croatia (for Croatian Nationals only)"). Quoted whole it swallows the
  // sentence it sits inside. The link still carries the full title.
  const shortTitle =
    jobTitle.length > 64
      ? `${jobTitle.slice(0, 64).replace(/[\s,–—-]+\S*$/, "")}…`
      : jobTitle;

  return (
    <div
      ref={rootRef}
      className="mt-4 rounded-lg border-2 border-brand-200 bg-brand-50 px-4 py-4 text-sm"
    >
      <p className="font-semibold text-brand-900">
        Your application to {orgName} is on its way.
      </p>
      {/* A UK reader took the paid video pitch, shown straight after Apply, for
          a fee to apply ("Stop scamming people", 2026-10-03). Charging work
          seekers to find work is unlawful there (Employment Agencies Act 1973
          s.6), so say plainly, before the price, that applying is free. */}
      <p className="mt-1 text-[12.5px] text-brand-800">
        Applying is always free. The video application is an optional extra.
      </p>

      {/* CM-471 (2026-10-14): the alert leads, Premium follows inline and is
          never auto-opened. CM-460 auto-opened the sidebar's dialog here and
          post-apply still converted 1.6% (4 clicks / 256 views, 1 checkout
          in 6.7 days) while the modal covered this form. Same order Finseats
          has run since 2026-10-08. */}
      <div className="mt-3">
        {captured ? (
          <p className="font-medium text-brand-800">
            {result?.state === "already_on"
              ? "That alert is already on — nothing else to do."
              : "Check your inbox — one click switches the alert on."}
          </p>
        ) : (
          <form
            action={formAction}
            onSubmit={(e) => {
              capture("alert_nudge_submit", { filters, surface: "job_post_apply" });
              // CM feedback (2026-09-30, exit-intent): "kept me repeating
              // things" — JobAlertCapture at the bottom of this same page asks
              // for an email too. Remember it here so that form can prefill
              // instead of asking cold a second time in the same visit.
              const email = new FormData(e.currentTarget).get("email");
              if (typeof email === "string" && email) {
                try {
                  localStorage.setItem("cm.lastemail.v1", email);
                } catch {
                  // Private mode / storage disabled — the second form just asks cold.
                }
              }
            }}
          >
            <label
              htmlFor="post-apply-alert-email"
              className="block text-[13.5px] font-medium text-brand-900"
            >
              Have {scopeLabel} come to you — one email, no account, only new
              listings.
            </label>
            <input type="hidden" name="filters" value={filters} />
            <div className="mt-2 flex flex-wrap gap-2">
              <input
                id="post-apply-alert-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="you@example.org"
                className="h-10 min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-3 text-[15px] text-ink placeholder:text-neutral-400 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
              />
              <button
                type="submit"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-md border border-brand-600 bg-white px-4 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pending ? "Setting up…" : "Email me these"}
              </button>
            </div>
            <label className="mt-2 flex items-start gap-2 text-[12px] leading-relaxed text-neutral-600">
              <input
                type="checkbox"
                name="terms"
                value="1"
                required
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-brand-600 focus:ring-brand-600"
              />
              <span>
                I accept the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 underline underline-offset-2"
                >
                  terms
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-700 underline underline-offset-2"
                >
                  privacy policy
                </a>
                .
              </span>
            </label>
            {result && !result.ok ? (
              <p
                role="alert"
                className="mt-2 rounded-md border border-stamp-100 bg-stamp-50 px-3 py-2 text-[13px] text-stamp-700"
              >
                {result.message}
              </p>
            ) : null}
          </form>
        )}
      </div>

      <div className="mt-3 rounded-md border border-brand-200 bg-white px-3.5 py-3">
        {isPaid ? null : (
          <p className="telex text-[9.5px] text-brand-700">
            Premium · {PREMIUM_PRICE_LABEL}, {PREMIUM_BILLING_NOTE}
          </p>
        )}
        <p
          className={`text-[13.5px] leading-relaxed text-neutral-700 ${
            isPaid ? "" : "mt-1.5"
          }`}
        >
          Make the next one impossible to ignore: a{" "}
          <span className="font-semibold text-ink">video application</span>{" "}
          written for{" "}
          <span className="font-medium text-ink">{shortTitle}</span> — we match
          your profile to the description, hand you the talking points, and you
          record a short video to attach to the cover letter.
        </p>
        <div className="mt-3">
          <VideoApplicationCta
            jobId={jobId}
            jobTitle={jobTitle}
            orgName={orgName}
            applyUrl={applyUrl}
            isPaid={isPaid}
            isAuthed={isAuthed}
            onCtaClick={() => capture("video_app_cta_click", { job_id: jobId })}
          />
        </div>
      </div>
    </div>
  );
}
